/* eslint-disable react-native/no-inline-styles */
import React, { useState, useRef } from 'react';
import { View, Text, StyleSheet, TextInput, Animated } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import ToggleSwitch from 'toggle-switch-react-native';
import i18n from 'i18n-js';

import { isAndroid, isPhone, isWeb } from '@helpers';

import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';
import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import { scale as initialScale } from '@components/frame';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const DefaultValue = ({
  pressHoverColor,
  item,
  item: { Component, isLable = true, small = true, width, topName, oneLine },
  C,
  setFocused,
  editable,
  threeColumns,
  threeColumnsForText,
  trackOffColor,
  thumbOffColor,
  multiline,
  leftShouldTakeFreeSpace,
  withoutPaddingLeft,
  withoutPositionAbsolute,
  scale = initialScale,
  font = initialFont,
}) => {
  const name = i18n.t(item.name);
  const maxLenght = isPhone ? 12 : small ? 10 : 25;
  const isTwoLine = oneLine ? false : item.isTime ? false : isLable ? name.length > maxLenght : false;

  const getDesignHeight = () => {
    if (item.isTime) return 49;
    if (isTwoLine) return 55;

    if (multiline) {
      const inputFontSize = scale(16);
      const lineHeight = inputFontSize * 1.4;
      const topPadding = scale(8);
      const bottomPadding = scale(10);
      const minLines = 2;

      return lineHeight * minLines + topPadding + bottomPadding;
    }

    return 41.5;
  };

  const designHeight = getDesignHeight();
  const baseHeight = scale(designHeight) - (isAndroid ? StyleSheet.hairlineWidth : 0);
  const lableWidth = scale((isPhone ? width || 110 : small ? 110 : width || 170) + 15);

  const renderValue = () => {
    if (item.isTime && !isWeb) {
      const color = item.animatedHeight
        ? item.animatedHeight.interpolate({
            inputRange: [0, scale(200)],
            outputRange: [C('Label/Light/Primary'), C('Link/Light/Primary')],
          })
        : C('Label/Light/Primary');
      return (
        <View
          style={[
            {
              width: '100%',
              flexDirection: 'row',
              alignItems: 'center',
              paddingLeft: lableWidth + scale(isPhone ? 4 : 13),
            },
            isTwoLine
              ? {}
              : {
                  height: '100%',
                  position: 'absolute',
                },
          ]}
        >
          <View style={{ flexDirection: 'row' }}>
            <Animated.Text style={[font['Time'], { color: color, marginTop: scale(3), fontVariant: ['tabular-nums'] }]}>
              <Text style={{ opacity: 0 }}>{item.value.h < 10 ? 0 : null}</Text>
              {Number(item.value.h)}
            </Animated.Text>
            <Text style={[font['Body'], { color: C('Label/Light/Tertiary'), marginLeft: scale(1), marginTop: scale(1) }]}>{i18n.t('h')}</Text>
          </View>
          <View style={{ flexDirection: 'row', marginLeft: scale(26) }}>
            <Animated.Text style={[font['Time'], { color: color, marginTop: scale(3), fontVariant: ['tabular-nums'] }]}>
              <Text style={{ opacity: 0 }}>{item.value.m < 10 ? 0 : null}</Text>
              {Number(item.value.m)}
            </Animated.Text>
            <Text style={[font['Body'], { color: C('Label/Light/Tertiary'), marginLeft: scale(1), marginTop: scale(1) }]}>{i18n.t('m')}</Text>
          </View>
          <View style={{ flexDirection: 'row', marginLeft: scale(26) }}>
            <Animated.Text style={[font['Time'], { color: color, marginTop: scale(3), fontVariant: ['tabular-nums'] }]}>
              <Text style={{ opacity: 0 }}>{item.value.s < 10 ? 0 : null}</Text>
              {Number(item.value.s)}
            </Animated.Text>
            <Text style={[font['Body'], { color: C('Label/Light/Tertiary'), marginLeft: scale(1), marginTop: scale(1) }]}>{i18n.t('s')}</Text>
          </View>
        </View>
      );
    }
    if (Component) {
      return <Component {...{ onChange: item.onChange, value: item.value, key: item.key, isTwoLine, lableWidth, baseHeight, isLable }} />;
    }

    if (item.type === 'text') {
      return (
        <Text
          style={[
            {
              color: C(pressHoverColor('Label/Light/Tertiary')),
              fontVariant: ['tabular-nums'],
            },
            font.Body,
            threeColumnsForText && !isPhone && { flex: 2 },
          ]}
        >
          {item.value}
        </Text>
      );
    }

    if (item.type === 'boolean') {
      return (
        <View
          style={[
            threeColumns && !isPhone
              ? { flex: 2 }
              : {
                  flex: 1,
                  alignItems: 'flex-end',
                  paddingRight: scale(11),
                },
            !isWeb && { justifyContent: 'center' },
          ]}
        >
          {item.loading ? (
            <View style={{ alignSelf: threeColumns && !isPhone ? 'flex-start' : 'flex-end' }}>
              <SmallLoading />
            </View>
          ) : (
            <ToggleSwitch
              scale={scale}
              size={'large'}
              onToggle={item.onChange}
              isOn={item.value}
              animationSpeed={0}
              disabled={!editable}
              {...(trackOffColor ? { offColor: trackOffColor } : {})}
              {...(thumbOffColor ? { thumbOffStyle: { backgroundColor: thumbOffColor } } : {})}
              // trackOnStyle={{ backgroundColor: C('Default/SystemGreen/Light') }}
              // thumbOnStyle={{ backgroundColor: C('System Background/Light/Primary') }}
            />
          )}
        </View>
      );
    }

    const maxLength = item.maxLength ? { maxLength: item.maxLength } : {};

    return (
      <TextInput
        multiline={multiline}
        editable={editable}
        onFocus={() => {
          setFocused(true);
          if (item.onClick) {
            item.onClick();
          }
        }}
        onSubmitEditing={() => {
          if (item.submit) {
            item.submit();
          }
        }}
        onBlur={() => setFocused(false)}
        onChangeText={item.onChange}
        value={item.value}
        ref={item.ref}
        placeholder={item?.placeholder && item?.placeholderAlreadyTranslated ? item.placeholder : i18n.t(item.placeholder || 'Optional')}
        placeholderTextColor={C('Label/Light/Quinary')}
        clearButtonMode={item.clearButtonMode || 'while-editing'}
        autoCapitalize={item.autoCapitalize || 'none'}
        autoCorrect={item.autoCorrect || false}
        secureTextEntry={item.secure}
        keyboardType={item.keyboardType || 'default'}
        autoComplete="one-time-code"
        style={[
          {
            height: baseHeight,
            flex: 1,
            color: C('Label/Dark/Primary'),
            fontSize: scale(16),
            width: '100%',
          },
          !withoutPositionAbsolute && {
            position: 'absolute',
          },
          isWeb && { paddingRight: scale(15), outline: 'none', border: 'none' },
          isTwoLine
            ? {
                paddingTop: scale(25),
                paddingLeft: scale(15),
              }
            : {
                paddingLeft: withoutPaddingLeft ? 0 : isLable ? lableWidth + scale(5) : scale(15),
              },
          multiline && {
            paddingTop: scale(8),
          },
          !editable && {
            cursor: 'default',
            color: C('Label/Light/Tertiary'),
          },
        ]}
        {...maxLength}
      />
    );
  };

  return [
    <View
      style={[
        {
          width: '100%',
          height: baseHeight,
        },
        isTwoLine
          ? {
              flexDirection: 'column',
            }
          : {
              alignItems: 'center',
              flexDirection: 'row',
            },
        topName && {
          flexDirection: 'column',
        },
      ]}
      key={item.key || item.name}
    >
      {isLable && (
        <Text
          // numberOfLines={item.isTime ? 2 : 1}
          // ellipsizeMode="tail"
          style={[
            {
              color: C(pressHoverColor('Label/Light/Primary')),
              paddingLeft: scale(15),
            },
            threeColumns && !isPhone && { flex: 1 },
            leftShouldTakeFreeSpace && !threeColumns && { flex: 1, paddingRight: scale(10) },
            isTwoLine
              ? {
                  width: '100%',
                  paddingTop: scale(5),
                }
              : {
                  width: lableWidth,
                },
            font.Body,
          ]}
        >
          {name}
        </Text>
      )}
      {item.placeholder && renderValue()}
      {!item.placeholder && <View style={[leftShouldTakeFreeSpace && !threeColumns && !item.placeholder && { flexShrink: 1 }, threeColumns && { flex: 2 }]}>
        {renderValue()}
      </View>}
    </View>,
    item.Component && !isWeb && (
      <Animated.View
        key={`animatedPart-` + item.name}
        style={[
          {
            paddingRight: item.noArrow ? 0 : scale(15),
            alignItems: 'center',
            flexDirection: 'row',
            height: item.animatedHeight ? item.animatedHeight : 0,
            width: '100%',
            borderTopColor: C('Hairline / Light / No trans'),
            borderTopWidth: StyleSheet.hairlineWidth,
            opacity: item.animatedHeight ? item.animatedHeight : 0,
          },
        ]}
      >
        <Component {...{ onChange: item.onChange, value: item.value, key: item.key, isOffset: item.isOffset }} />
      </Animated.View>
    ),
  ];
};

const AutocompleteItem = ({ C, item, onPress, list, index, scale = initialScale, font = initialFont }) => (
  <TouchableElement
    {...{
      hitSlop: { top: 0, bottom: 0, left: 0, right: 0 },
      onPress: () => onPress(item),
    }}
  >
    {({ pressHoverColor, boxStyle }) => (
      <View style={[boxStyle, { width: '100%', paddingLeft: scale(15) }]}>
        <View
          style={{
            width: '100%',
            height: scale(66),
            borderBottomColor: C('Hairline/Light/No trans'),
            borderBottomWidth: index === list?.length - 1 ? 0 : StyleSheet.hairlineWidth,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: C(pressHoverColor('Background/Light/Field')),
          }}
        >
          <View
            style={{
              width: scale(66),
              height: scale(50),
              backgroundColor: C('Overlay/Light/Picture white'),
              borderRadius: scale(4),
              overflow: 'hidden',
            }}
          >
            <View style={{ width: scale(66), height: scale(50), backgroundColor: C('Overlay/Light/Dim light') }}></View>
          </View>
          <View style={{ flex: 1, paddingLeft: scale(10) }}>
            <Text style={[font['Headline'], { color: C('Label/Light/Primary'), marginTop: scale(4) }]}>{item.topLine} </Text>
            <Text style={[font['Body'], { color: C('Label/Light/Tertiary'), marginTop: scale(-2) }]}>{item.middleLine} </Text>
            <Text style={[font['Body'], { color: C('Label/Light/Tertiary'), marginTop: scale(-2) }]}>{item.bottomLine} </Text>
          </View>
          <View style={{ width: scale(20), marginRight: scale(20) }}>
            <Svg icon="info" width={scale(20)} height={scale(20)} color="Link/Light/Primary" />
          </View>
        </View>
      </View>
    )}
  </TouchableElement>
);

export default (props) => {
  const {
    item,
    index,
    array,
    C,
    roundTop,
    roundBottom,
    topBorder = true,
    bottomBorder = false,
    bottomBorderBox = false,
    topBorderBox = false,
    bg = 'Background / Light / Field',
    pressStyle = true,
    editable = true,
    multiline = false,
    threeColumns = false,
    threeColumnsForText = false,
    leftShouldTakeFreeSpace = true,
    customStyles,
    trackOffColor,
    thumbOffColor,
    withoutPressHoverColor = false,
    withoutPaddingLeft = false,
    withoutPositionAbsolute = false,
    font = initialFont,
    scale = initialScale,
  } = props;
  let styles = {};
  const [isFocused, setFocused] = useState(false);

  if (roundTop) {
    styles = {
      ...styles,
      borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
      borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
    };
  }
  if (roundBottom) {
    styles = {
      ...styles,
      borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
      borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
    };
  }

  return (
    <TouchableElement
      {...{
        testID: item.testID,
        disabled: item.autocomplete || (editable && !item.onClick),
        pressStyle,
        hitSlop: { top: 0, bottom: 0, left: 0, right: 0 },
        onPress: () => {
          if (item.onClick && item.isTime) {
            item.onClick();
          }
        },
      }}
    >
      {({ pressHoverColor: pressHoverColorFN, boxStyle }) => {
        const pressHoverColor = (c) => (withoutPressHoverColor || isFocused || (item.type === 'boolean' && !item.onClick) ? c : pressHoverColorFN(c));
        return (
          <View style={[item.topName && { marginBottom: scale(28), paddingHorizontal: scale(isPhone ? 15 : 0) }, customStyles]}>
            {item.topName && (
              <Text
                // numberOfLines={1}
                // ellipsizeMode="tail"
                style={[
                  {
                    color: C('Label/Light/Tertiary'),
                    paddingLeft: scale(15),
                    width: '100%',
                    marginBottom: scale(5),
                    textTransform: 'uppercase',
                  },
                  font.Footnote,
                ]}
              >
                {i18n.t(item.name)}
              </Text>
            )}
            <View
              style={[
                boxStyle,
                {
                  marginTop: isAndroid ? StyleSheet.hairlineWidth : 0,
                  position: 'relative',
                  backgroundColor: C(pressHoverColor(bg)),
                },
                topBorderBox &&
                  index === 0 && {
                    borderTopColor: C('Hairline / Light / No trans'),
                    borderTopWidth: StyleSheet.hairlineWidth,
                  },
                bottomBorderBox &&
                  index === array.length - 1 && {
                    borderBottomColor: C('Hairline / Light / No trans'),
                    borderBottomWidth: StyleSheet.hairlineWidth,
                  },
                styles,
              ]}
            >
              <View
                style={{
                  paddingLeft: scale(15),
                  width: '100%',
                }}
              >
                <View
                  style={{
                    borderTopColor: C('Hairline / Light / No trans'),
                    borderTopWidth: topBorder ? StyleSheet.hairlineWidth : 0,
                    width: '100%',
                  }}
                />
              </View>
              <DefaultValue
                {...{
                  pressHoverColor,
                  item,
                  C,
                  setFocused,
                  editable,
                  threeColumns,
                  trackOffColor,
                  thumbOffColor,
                  multiline,
                  scale,
                  font,
                  threeColumnsForText,
                  leftShouldTakeFreeSpace,
                  withoutPaddingLeft,
                  withoutPositionAbsolute,
                }}
              />
              {item.autocomplete ? (
                item.autocompleteLoading ? (
                  <View style={{ width: '100%', height: scale(100), justifyContent: 'center', alignContent: 'center' }}>
                    <SmallLoading />
                  </View>
                ) : (
                  [
                    item.showNewButton && (
                      <TouchableElement
                        {...{
                          hitSlop: { top: 0, bottom: 0, left: 0, right: 0 },
                          onPress: item.onNewPress,
                        }}
                      >
                        {({ boxStyle }) => (
                          <View style={[boxStyle, { position: 'absolute', right: scale(20), top: scale(11) }]} key="new-button">
                            <Text style={[font['Body'], { color: C('Link/Light/Special') }]}>{i18n.t('New')}</Text>
                          </View>
                        )}
                      </TouchableElement>
                    ),
                    <View style={{ width: '100%', maxHeight: scale(300) }} key="autocomplete-list">
                      <FlashList
                        data={item.autocompleteList}
                        renderItem={({ item: i, index }) => (
                          <AutocompleteItem {...{ item: i, C, index, onPress: item.onAutocompletePress, list: item.autocompleteList, scale, font }} />
                        )}
                        keyExtractor={(item) => item.id}
                      />
                    </View>,
                  ]
                )
              ) : null}
              <View
                style={{
                  paddingLeft: scale(15),
                  width: '100%',
                }}
              >
                <View
                  style={{
                    borderTopColor: C('Hairline / Light / No trans'),
                    borderTopWidth: bottomBorder ? StyleSheet.hairlineWidth : 0,
                    width: '100%',
                  }}
                />
              </View>
            </View>
          </View>
        );
      }}
    </TouchableElement>
  );
};
